/**
 * Design Tokens: Shadows & Effects
 * Auto-generated from Figma Design System: Crop Circle System
 *
 * Shadow and effect tokens for elevation and depth.
 * Includes both shadow definitions and blur effects from Figma.
 */

import { ViewStyle } from 'react-native';

/**
 * Shadow definitions for React Native
 * Note: React Native shadows work differently on iOS vs Android
 */
export const shadows = {
  /** No shadow */
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0, // Android
  },

  /** Small shadow - Level 1 elevation */
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1, // Android
  },

  /** Medium shadow - Level 2 elevation */
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2, // Android
  },

  /** Large shadow - Level 3 elevation */
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4, // Android
  },

  /** Extra large shadow - Level 4 elevation */
  xl: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8, // Android
  },

  /** Huge shadow - Level 5 elevation */
  '2xl': {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 12, // Android
  },
} as const;

/**
 * Blur effects
 * From Figma BACKGROUND_BLUR effects on Main Navigation component
 */
export const blur = {
  /** No blur */
  none: 0,

  /** Small blur */
  sm: 4,

  /** Medium blur */
  md: 8,

  /** Large blur - Used in Main Navigation (12px from Figma) */
  lg: 12,

  /** Extra large blur */
  xl: 16,

  /** Huge blur */
  '2xl': 24,
} as const;

/**
 * Component-specific shadow effects
 */
export const componentShadows = {
  /**
   * Main Navigation
   * Uses background blur effect (12px) from Figma
   */
  mainNavigation: {
    blur: blur.lg, // 12px background blur
  },

  /**
   * Card shadows
   */
  card: {
    default: shadows.md,
    hover: shadows.lg,
  },

  /**
   * Button shadows
   */
  button: {
    default: shadows.sm,
    pressed: shadows.none,
  },

  /**
   * Modal/Dialog shadows
   */
  modal: {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    content: shadows['2xl'],
  },

  /**
   * Dropdown shadows
   */
  dropdown: {
    default: shadows.lg,
  },
} as const;

/**
 * Helper function to get combined shadow styles
 * @param shadowKey - Key from the shadows object
 * @returns ViewStyle with shadow properties
 */
export const getShadowStyle = (
  shadowKey: keyof typeof shadows
): ViewStyle => {
  return shadows[shadowKey];
};

/**
 * Shadow types for TypeScript
 */
export type ShadowToken = typeof shadows;
export type ShadowKey = keyof typeof shadows;
export type BlurToken = typeof blur;
export type BlurKey = keyof typeof blur;
